import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, TrendingUp, ArrowRight, Check, Shield, Zap, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return { minLength, hasUppercase, hasNumber, hasSpecial, isValid: minLength && hasUppercase && hasNumber && hasSpecial };
  };

  const passwordStrength = validatePassword(formData.password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!passwordStrength.isValid) {
      toast.error('Password does not meet security requirements');
      return;
    }

    if (!agreed) {
      toast.error('Please agree to the terms and conditions');
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/auth/register`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
        password: formData.password
      });
      
      toast.success('Registration successful! Please check your email for the verification code.');
      
      // Navigate to verify email page
      navigate('/verify-email', { 
        state: { 
          email: formData.email
        } 
      });
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'Earn daily profits through automated trading',
    'Secure and encrypted platform',
    'Fast deposit and withdrawal',
    '24/7 customer support',
    'Track your earnings in real-time'
  ];

  return (
    <div className="min-h-screen flex">
      <div className="animated-bg" />
      <div className="noise-overlay" />
      
      {/* Left Panel - Benefits */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-emerald-500/10" />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="relative max-w-lg"
        >
          <h2 className="text-3xl font-bold font-['Outfit'] text-white mb-4">
            Start Your Trading Journey
          </h2>
          <p className="text-slate-400 mb-8 leading-relaxed">
            Join our platform and start earning daily profits through automated cryptocurrency trading.
          </p>

          <div className="space-y-4 mb-12">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3"
              >
                <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Check className="w-4 h-4 text-emerald-400" />
                </div>
                <span className="text-slate-300">{benefit}</span>
              </motion.div>
            ))}
          </div>

          {/* Features */}
          <div className="grid grid-cols-3 gap-4">
            <div className="glass-card p-4 text-center">
              <Shield className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
              <div className="text-sm text-white font-medium">Secure</div>
              <div className="text-xs text-slate-400">256-bit SSL</div>
            </div>
            <div className="glass-card p-4 text-center">
              <Zap className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <div className="text-sm text-white font-medium">Fast</div>
              <div className="text-xs text-slate-400">Instant deposits</div>
            </div>
            <div className="glass-card p-4 text-center">
              <DollarSign className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <div className="text-sm text-white font-medium">Profitable</div>
              <div className="text-xs text-slate-400">Daily returns</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md py-8"
        >
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-emerald-400 flex items-center justify-center">
              <TrendingUp className="w-7 h-7 text-gray-900" />
            </div>
            <span className="text-2xl font-bold font-['Outfit'] gradient-text">
              MetaBlockify
            </span>
          </Link>

          <h1 className="text-3xl font-bold font-['Outfit'] text-white mb-2">
            Create Account
          </h1>
          <p className="text-slate-400 mb-8">
            Join our automated trading platform today
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                placeholder="John Doe"
                required
                data-testid="register-name"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                placeholder="john@example.com"
                required
                data-testid="register-email"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                placeholder="+1 234 567 8900"
                required
                data-testid="register-phone"
              />
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Country
              </label>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-4 text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all appearance-none"
                required
                data-testid="register-country"
              >
                <option value="" disabled>Select your country</option>
                <option value="US">United States</option>
                <option value="GB">United Kingdom</option>
                <option value="CA">Canada</option>
                <option value="AU">Australia</option>
                <option value="DE">Germany</option>
                <option value="FR">France</option>
                <option value="JP">Japan</option>
                <option value="KR">South Korea</option>
                <option value="SG">Singapore</option>
                <option value="AE">UAE</option>
                <option value="IN">India</option>
                <option value="BR">Brazil</option>
                <option value="NG">Nigeria</option>
                <option value="ZA">South Africa</option>
                <option value="MX">Mexico</option>
                <option value="CH">Switzerland</option>
                <option value="NL">Netherlands</option>
                <option value="SE">Sweden</option>
                <option value="HK">Hong Kong</option>
                <option value="MY">Malaysia</option>
                <option value="PH">Philippines</option>
                <option value="ID">Indonesia</option>
                <option value="TH">Thailand</option>
                <option value="VN">Vietnam</option>
                <option value="PK">Pakistan</option>
                <option value="BD">Bangladesh</option>
                <option value="EG">Egypt</option>
                <option value="KE">Kenya</option>
                <option value="GH">Ghana</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-4 pr-12 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                  placeholder="Create a strong password"
                  required
                  minLength={8}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {/* Password strength indicators */}
              {formData.password && (
                <div className="mt-2 space-y-1">
                  <div className="flex items-center gap-2 text-xs">
                    <div className={`w-3 h-3 rounded-full ${passwordStrength.minLength ? 'bg-emerald-500' : 'bg-slate-600'}`} />
                    <span className={passwordStrength.minLength ? 'text-emerald-400' : 'text-slate-500'}>At least 8 characters</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className={`w-3 h-3 rounded-full ${passwordStrength.hasUppercase ? 'bg-emerald-500' : 'bg-slate-600'}`} />
                    <span className={passwordStrength.hasUppercase ? 'text-emerald-400' : 'text-slate-500'}>One uppercase letter</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className={`w-3 h-3 rounded-full ${passwordStrength.hasNumber ? 'bg-emerald-500' : 'bg-slate-600'}`} />
                    <span className={passwordStrength.hasNumber ? 'text-emerald-400' : 'text-slate-500'}>One number</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className={`w-3 h-3 rounded-full ${passwordStrength.hasSpecial ? 'bg-emerald-500' : 'bg-slate-600'}`} />
                    <span className={passwordStrength.hasSpecial ? 'text-emerald-400' : 'text-slate-500'}>One special character</span>
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-4 pr-12 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                  placeholder="Confirm your password"
                  required
                  data-testid="register-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
              )}
            </div>

            {/* Terms */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="terms"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500 cursor-pointer"
                data-testid="register-terms"
              />
              <label htmlFor="terms" className="text-sm text-slate-400 cursor-pointer">
                I agree to the{' '}
                <Link to="/terms" className="text-cyan-400 hover:text-cyan-300">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300">
                  Privacy Policy
                </Link>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !passwordStrength.isValid}
              className="btn-primary w-full flex items-center justify-center gap-2 py-4 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Create Account
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Verification Notice */}
          <div className="mt-6 p-4 bg-cyan-500/10 rounded-xl border border-cyan-500/20">
            <p className="text-cyan-400 text-sm text-center">
              You'll receive a verification code to confirm your email address.
            </p>
          </div>

          {/* Sign In Link */}
          <p className="text-center text-slate-400 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-cyan-400 hover:text-cyan-300 font-medium">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterPage;
